import { create } from "zustand";
import type { User, Session, Provider } from "@supabase/supabase-js";
import { supabase } from "../services/supabase";
import { fetchProfileAndWallet } from "../services/profileWalletService";
import type { Profile, Wallet } from "../types";

interface AuthState {
  user: User | null;
  profile: Profile | null;
  wallet: Wallet | null;
  loading: boolean;
  initialized: boolean;

  initialize: () => Promise<void>;
  signInWithOAuth: (provider: Provider, redirectPath?: string) => Promise<void>;
  signInWithPhone: (phone: string) => Promise<void>;
  verifyPhoneOtp: (phone: string, token: string) => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string, data?: Record<string, any>) => Promise<void>;
  signOut: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  // 兼容历史代码：部分页面引用了 isLoading/setSession
  isLoading: boolean;
  setSession: (session: Session | null) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  wallet: null,
  loading: false,
  initialized: false,
  // 兼容历史代码：部分页面引用了 isLoading
  isLoading: false,
  setSession: (session: Session | null) => set({ user: session?.user ?? null }),

  initialize: async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.user) {
      set({ user: session.user });
      await get().fetchProfile();
    }
    set({ initialized: true });

    supabase.auth.onAuthStateChange(async (_event, session) => {
      set({ user: session?.user || null });
      if (session?.user) {
        await get().fetchProfile();
      } else {
        set({ profile: null, wallet: null });
      }
    });
  },

  signInWithOAuth: async (provider: any, redirectPath: string = "/app") => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: provider,
      options: {
        // redirectPath 由调用方经 safeRedirectPath 校验；Supabase 的 Redirect URLs 白名单需包含目标路径
        redirectTo: `${window.location.origin}${redirectPath}`,
      }
    });
    if (error) throw error;
  },

  signInWithPhone: async (phone: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      phone,
    });
    if (error) throw error;
  },

  verifyPhoneOtp: async (phone: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      phone,
      token,
      type: 'sms',
    });
    if (error) throw error;
  },

  signInWithMagicLink: async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/app`,
      }
    });
    if (error) throw error;
  },

  signInWithPassword: async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  },

  signUpWithPassword: async (email: string, password: string, data?: any) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data,
      },
    });
    if (error) throw error;
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ user: null, profile: null, wallet: null });
  },

  fetchProfile: async () => {
    const { user } = get();
    if (!user) return;

    const { profile, wallet } = await fetchProfileAndWallet(user.id);
    set({ profile, wallet });
  },
}));
